/**
 * SourcesBar — barra de "Fuentes" verificable bajo cada respuesta de ARIA.
 *
 * Tier 1 (verificabilidad). Sustituye las chips planas de nombres de tool por:
 *   - una chip por DEPARTAMENTO consultado, clicable → deep-link al módulo
 *     (factura, gasto, asiento…) vía onSourceClick(route).
 *   - un popover "Cómo lo sé" con la confianza CALCULADA de la respuesta y sus
 *     salvedades, la confianza del routing, los departamentos, las tools
 *     concretas usadas y el modelo (tier) que respondió.
 *
 * Router-free: la navegación se delega en onSourceClick para no acoplar este
 * componente (ni MessageMarkdown, que lo monta) a react-router. Si no hay
 * `sources`, no renderiza nada — el fallback de texto vive en MessageMarkdown.
 */

import { Info, ExternalLink, AlertTriangle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getDepartment, getDepartmentRoute } from './departments';

const TIER_LABEL = { haiku: 'Rápido (Haiku)', sonnet: 'Equilibrado (Sonnet)', opus: 'Avanzado (Opus)' };

function confidenceTone(pct) {
  if (pct >= 70) return 'text-success';
  if (pct >= 40) return 'text-warning';
  return 'text-muted-foreground';
}

/**
 * E1 — confianza CALCULADA de la respuesta (`answer_confidence` del fan-out).
 *
 * No es la del enrutado: sale de señales objetivas del turno (un especialista
 * que reventó, un hueco declarado, una tool sin datos, cifras que el juez de
 * grounding marcó sin respaldo). Se muestra sólo cuando NO es alta — un aviso
 * que salta siempre deja de leerse, y "alta" es el caso normal.
 */
const ANSWER_CONFIDENCE_LABEL = { alta: 'Alta', media: 'Media', baja: 'Baja' };
const ANSWER_CONFIDENCE_TONE = {
  alta: 'text-success',
  media: 'text-warning',
  baja: 'text-destructive',
};

export default function SourcesBar({ sources = [], meta, onSourceClick }) {
  if (!sources.length) return null;

  const pct = typeof meta?.confidence === 'number' ? Math.round(meta.confidence * 100) : null;
  const answer = meta?.answerConfidence;
  const answerLevel = answer?.confidence;
  const caveats = Array.isArray(answer?.caveats) ? answer.caveats : [];
  // Sólo se avisa fuera de la barra cuando la respuesta NO es de confianza alta.
  const flagged = answerLevel === 'media' || answerLevel === 'baja';

  return (
    <div className="mt-2 pt-1.5 border-t border-border flex items-center flex-wrap gap-1">
      <span className="text-[10px] text-muted-foreground">Fuentes:</span>

      {sources.map((s, i) => {
        const dep = getDepartment(s.domain);
        const Icon = dep.Icon;
        const route = getDepartmentRoute(s.domain);
        const clickable = !!route && !!onSourceClick;
        const label = dep.label;
        const className = `inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${dep.tone} ${
          clickable ? 'cursor-pointer hover:opacity-80' : ''
        }`;
        const inner = (
          <>
            <Icon className="h-2.5 w-2.5" />
            <span>{label}</span>
            {clickable && <ExternalLink className="h-2 w-2 opacity-60" />}
          </>
        );
        return clickable ? (
          <button
            key={i}
            type="button"
            className={className}
            onClick={() => onSourceClick(route)}
            title={`Abrir ${label}${s.tools?.length ? ` · ${s.tools.join(', ')}` : ''}`}
          >
            {inner}
          </button>
        ) : (
          <span key={i} className={className} title={s.tools?.join(', ')}>{inner}</span>
        );
      })}

      {/* Confianza calculada: sólo visible fuera del popover si NO es alta. */}
      {flagged && (
        <span
          className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-muted ${ANSWER_CONFIDENCE_TONE[answerLevel]}`}
          title={caveats.join(' · ') || 'Confianza calculada de la respuesta'}
        >
          <AlertTriangle className="h-2.5 w-2.5" />
          Confianza {ANSWER_CONFIDENCE_LABEL[answerLevel].toLowerCase()}
        </span>
      )}

      {/* "Cómo lo sé" — explicabilidad de la respuesta */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            title="Cómo lo sé"
          >
            <Info className="h-2.5 w-2.5" />
            Cómo lo sé
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 text-xs">
          <p className="font-medium text-foreground mb-2">Cómo obtuve esta respuesta</p>

          {answerLevel && (
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Confianza de la respuesta</span>
                <span className={`font-semibold ${ANSWER_CONFIDENCE_TONE[answerLevel] || ''}`}>
                  {ANSWER_CONFIDENCE_LABEL[answerLevel] || answerLevel}
                </span>
              </div>
              {caveats.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-[10px] text-muted-foreground list-disc pl-3.5">
                  {caveats.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
            </div>
          )}

          {pct !== null && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Confianza del enrutado</span>
              <span className={`font-semibold ${confidenceTone(pct)}`}>{pct}%</span>
            </div>
          )}

          <div className="space-y-1.5">
            {sources.map((s, i) => {
              const dep = getDepartment(s.domain);
              const Icon = dep.Icon;
              return (
                <div key={i} className="flex items-start gap-1.5">
                  <span className={`mt-px inline-flex h-4 w-4 shrink-0 items-center justify-center rounded ${dep.tone}`}>
                    <Icon className="h-2.5 w-2.5" />
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-foreground">{dep.label}</span>
                    {s.tools?.length > 0 && (
                      <span className="text-muted-foreground"> · {s.tools.join(', ')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {meta?.modelTier && TIER_LABEL[meta.modelTier] && (
            <p className="mt-2 pt-1.5 border-t border-border text-[10px] text-muted-foreground">
              Modelo: {TIER_LABEL[meta.modelTier]}
            </p>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
